<template>
    <div class="calendar-pane">
        <div class="calendar-pane__header-row">
            <div class="calendar-pane__header-group">
                <QBtn flat
                      dense
                      round
                      icon="mdi-chevron-left"
                      size="md"
                      @click.stop.prevent="emitNavigateMonth(-1)" />
                <QBtn flat
                      dense
                      no-caps
                      class="calendar-pane__header-label q-ma-none q-pa-none"
                      @click.stop.prevent="openMonthMenu">
                    {{ currentMonthLabel }}
                </QBtn>
                <QBtn flat
                      dense
                      round
                      icon="mdi-chevron-right"
                      size="md"
                      @click.stop.prevent="emitNavigateMonth(1)" />
            </div>

            <div class="calendar-pane__header-group">
                <QBtn flat
                      dense
                      round
                      icon="mdi-chevron-left"
                      size="md"
                      @click.stop.prevent="selectYear(view.year - 1)" />
                <QBtn flat
                      dense
                      no-caps
                      class="calendar-pane__header-label q-ma-none q-pa-none"
                      @click.stop.prevent="openYearMenu">
                    {{ view.year }}
                </QBtn>
                <QBtn flat
                      dense
                      round
                      icon="mdi-chevron-right"
                      size="md"
                      @click.stop.prevent="selectYear(view.year + 1)" />
            </div>
        </div>
        <QMenu v-if="monthMenu"
               v-model="monthMenu"
               :target="monthActivatorRef"
               no-parent-event
               anchor="bottom middle"
               self="top middle"
               @show="onMonthMenuShow">
            <QList ref="monthListRef" dense class="calendar-pane__menu-list">
                <QItem v-for="month in monthOptions"
                       :key="month.value"
                       clickable
                       :active="month.value === view.month"
                       active-class="bg-primary text-white"
                       @click="selectMonth(month.value)">
                    <QItemSection>{{ month.label }}</QItemSection>
                </QItem>
            </QList>
        </QMenu>

        <QMenu v-if="yearMenu"
               v-model="yearMenu"
               :target="yearActivatorRef"
               no-parent-event
               anchor="bottom middle"
               self="top middle"
               @show="onYearMenuShow">
            <QList ref="yearListRef" dense class="calendar-pane__menu-list">
                <QItem v-for="year in yearOptions"
                       :key="year"
                       clickable
                       :active="year === view.year"
                       active-class="bg-primary text-white"
                       @click="selectYear(year)">
                    <QItemSection>{{ year }}</QItemSection>
                </QItem>
            </QList>
        </QMenu>

        <div class="calendar-pane__weekday-grid">
            <div v-for="weekday in weekdayLabels"
                 :key="weekday"
                 class="calendar-pane__weekday">
                {{ weekday }}
            </div>
        </div>

        <div class="calendar-pane__day-grid-viewport">
            <Transition :name="transitionName">
                <div class="calendar-pane__day-grid"
                     :key="gridKey"
                     @mouseleave="emitHoverDay(null)">
                    <QBtn v-for="cell in dayCells"
                          :key="cell.id"
                          flat
                          dense
                          no-caps
                          :disable="cell.isDisabled"
                          :ripple="!cell.isDisabled"
                          :class="[
          'calendar-pane__day',
          {
            'is-placeholder': cell.isPlaceholder,
            'is-outside-month': cell.isOutsideMonth,
            'is-today': cell.isToday,
            'is-in-range': cell.isInRange && !cell.isStart && !cell.isEnd,
            'is-start': cell.isStart,
            'is-end': cell.isEnd,
            'is-range-start-edge': cell.isRangeStartEdge,
            'is-range-end-edge': cell.isRangeEndEdge,
            'is-preview-edge': cell.isPreviewEdge,
            'has-range-band': cell.hasRangeBand
          }
        ]"
                          @mouseenter="emitHoverDay(cell)"
                          @click="emitPickDay(cell.dayKey)">
                        <span class="calendar-pane__day-label">{{ cell.label }}</span>
                    </QBtn>
                </div>
            </Transition>
        </div>
    </div>
</template>

<script>
    import { computed, defineComponent, nextTick, ref } from 'vue';
    import { QBtn, QItem, QItemSection, QList, QMenu } from 'quasar';

    export default defineComponent({
        name: 'CalendarMonthPane',
        components: {
            QBtn,
            QItem,
            QItemSection,
            QList,
            QMenu,
        },
        props: {
            pane: {
                type: String,
                required: true,
            },
            view: {
                type: Object,
                required: true,
            },
            monthOptions: {
                type: Array,
                required: true,
            },
            yearOptions: {
                type: Array,
                required: true,
            },
            weekdayLabels: {
                type: Array,
                required: true,
            },
            dayCells: {
                type: Array,
                required: true,
            },
            transitionName: {
                type: String,
                default: 'month-slide-none',
            },
            gridKey: {
                type: String,
                default: '',
            },
        },
        emits: ['navigate-month', 'set-month', 'set-year', 'pick-day', 'hover-day'],
        setup(props, { emit }) {
            const monthMenu = ref(false);
            const yearMenu = ref(false);
            const monthActivatorRef = ref(null);
            const yearActivatorRef = ref(null);
            const monthListRef = ref(null);
            const yearListRef = ref(null);

            const currentMonthLabel = computed(() => {
                const selected = props.monthOptions.find((month) => month.value === props.view.month);
                return selected ? selected.label : '';
            });

            function emitNavigateMonth(delta) {
                emit('navigate-month', { pane: props.pane, delta });
            }

            function openMonthMenu(event) {
                monthActivatorRef.value = event.currentTarget;
                monthMenu.value = true;
            }

            function openYearMenu(event) {
                yearActivatorRef.value = event.currentTarget;
                yearMenu.value = true;
            }

            function selectMonth(month) {
                monthMenu.value = false;
                emit('set-month', { pane: props.pane, month });
            }

            function selectYear(year) {
                yearMenu.value = false;
                emit('set-year', { pane: props.pane, year });
            }

            function emitPickDay(dayKey) {
                emit('pick-day', { pane: props.pane, dayKey });
            }

            function emitHoverDay(cell) {
                emit('hover-day', {
                    pane: props.pane,
                    dayKey: cell?.dayKey || null,
                    isDisabled: Boolean(cell?.isDisabled || cell?.isPlaceholder),
                });
            }

            function scrollActiveIntoView(listRef) {
                const listEl = listRef?.value?.$el || listRef?.value;
                if (!listEl || typeof listEl.querySelector !== 'function') return;
                const activeEl = listEl.querySelector('.q-item--active');
                if (!activeEl || typeof activeEl.scrollIntoView !== 'function') return;
                activeEl.scrollIntoView({ block: 'center' });
            }

            function onMonthMenuShow() {
                nextTick(() => scrollActiveIntoView(monthListRef));
            }

            function onYearMenuShow() {
                nextTick(() => scrollActiveIntoView(yearListRef));
            }

            return {
                monthMenu,
                yearMenu,
                monthActivatorRef,
                yearActivatorRef,
                monthListRef,
                yearListRef,
                currentMonthLabel,
                emitNavigateMonth,
                openMonthMenu,
                openYearMenu,
                selectMonth,
                selectYear,
                emitPickDay,
                emitHoverDay,
                onMonthMenuShow,
                onYearMenuShow,
            };
        },
    });
</script>

<style scoped>
    .calendar-pane {
        width: 262px;
    }

    .calendar-pane__header-row {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 8px;
        margin-bottom: 6px;
    }

    .calendar-pane__header-group {
        display: grid;
        grid-template-columns: 24px 1fr 24px;
        align-items: center;
        gap: 3px;
        min-width: 0;
        flex: 1 1 0;
    }

    .calendar-pane__header-label {
        font-weight: 600;
        font-size: 16px;
        min-height: 24px;
    }

    .calendar-pane__menu-list {
        max-height: 220px;
        min-width: 136px;
    }

    .calendar-pane__weekday-grid,
    .calendar-pane__day-grid {
        display: grid;
        grid-template-columns: repeat(7, minmax(0, 1fr));
    }

    .calendar-pane__day-grid-viewport {
        min-height: 180px;
        position: relative;
        overflow: hidden;
    }

    .calendar-pane__day-grid {
        grid-auto-rows: 30px;
        min-height: 180px;
        align-content: start;
        position: relative;
    }

    .calendar-pane__weekday {
        text-align: center;
        color: #969696;
        font-size: 11px;
        padding: 4px 0;
    }

        .calendar-pane__day {
        position: relative;
        padding: 0;
        border: 0;
        background: transparent;
        width: 100%;
        height: 30px;
        min-height: 30px;
        font-size: 19px;
        font-weight: 400;
        cursor: pointer;
        border-radius: 0;
    }

        .calendar-pane__day :deep(.q-btn__content) {
            width: 100%;
            height: 100%;
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 0;
        }

        .calendar-pane__day :deep(.q-focus-helper) {
            top: 50%;
            left: 50%;
            width: 24px;
            height: 24px;
            transform: translate(-50%, -50%);
            border-radius: 50%;
        }

        .calendar-pane__day-label {
            position: relative;
            z-index: 2;
            box-sizing: border-box;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            width: 24px;
            height: 24px;
            border-radius: 50%;
            font-size: 14px;
            font-weight: 400;
            line-height: 1;
        }

        .calendar-pane__day.is-placeholder {
            visibility: hidden;
            pointer-events: none;
        }

        .calendar-pane__day.is-outside-month .calendar-pane__day-label {
            color: #b9c0cb;
        }

        .calendar-pane__day.is-in-range {
            background: var(--v-activeBackground-base);
        }

        .calendar-pane__day.has-range-band.is-start,
        .calendar-pane__day.has-range-band.is-end {
            background: var(--v-activeBackground-base);
        }

        .calendar-pane__day.is-range-start-edge {
            border-top-left-radius: 8px;
            border-bottom-left-radius: 8px;
        }

        .calendar-pane__day.is-range-end-edge {
            border-top-right-radius: 8px;
            border-bottom-right-radius: 8px;
        }

        .calendar-pane__day.is-range-start-edge.is-range-end-edge {
            border-radius: 8px;
        }

            .calendar-pane__day.is-start .calendar-pane__day-label,
            .calendar-pane__day.is-end .calendar-pane__day-label {
                background: var(--v-primary-base);
                color: #fff;
            }

            .calendar-pane__day.is-preview-edge:not(.is-start):not(.is-end) .calendar-pane__day-label {
                border: 2px solid var(--v-primary-base);
                background: #fff;
                color: var(--v-primary-base);
            }

        .calendar-pane__day.is-today .calendar-pane__day-label {
            border: 2px solid var(--v-primary-base);
        }

        .calendar-pane__day:disabled {
            color: #c5c5c5;
            cursor: not-allowed;
        }

        .calendar-pane__day:focus,
        .calendar-pane__day:focus-visible {
            outline: none;
        }

    .month-slide-forward-enter-active,
    .month-slide-forward-leave-active,
    .month-slide-backward-enter-active,
    .month-slide-backward-leave-active {
        transition: transform 320ms ease;
        position: absolute;
        inset: 0;
        width: 100%;
    }

    .month-slide-forward-enter-from {
        transform: translateX(100%);
    }

    .month-slide-forward-leave-to {
        transform: translateX(-100%);
    }

    .month-slide-backward-enter-from {
        transform: translateX(-100%);
    }

    .month-slide-backward-leave-to {
        transform: translateX(100%);
    }

    .month-slide-none-enter-active,
    .month-slide-none-leave-active {
        transition: none;
    }
</style>
